import React from 'react';
import { Check, ExternalLink } from 'lucide-react';
import { getForecastTerm } from '@/lib/terminology-config';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useApp } from '@/contexts/AppContext';
import { useOnboarding, ONBOARDING_STEPS } from '@/contexts/OnboardingContext';
import type { OnboardingStepId } from '@/contexts/OnboardingContext';
import { toast } from 'sonner';

// ── Edit navigation targets ───────────────────────────────────────────────────
// CRITICAL: Edit links close the assistant and navigate to the REAL settings
// page. They do NOT reopen the assistant at that step.

const EDIT_NAVIGATION: Record<OnboardingStepId, string> = {
  'general-settings': 'admin-general-settings',
  'data-sources': 'admin-data-sources',
  'roles-permissions': 'admin-roles-permissions',
  'user-management': 'admin-users',
  'dashboard-layout': 'admin-dashboard-layout',
};

// ── Completion screen ─────────────────────────────────────────────────────────

export const CompletionScreen: React.FC = () => {
  const { handleNavigate } = useApp(); // Global navigate — wired from AppContent
  const { completeOnboarding, skippedSteps, getStepStatus } = useOnboarding();

  // Complete onboarding: show skipped-steps toast, navigate to Admin Dashboard
  const handleConfirm = () => {
    // Build list of skipped steps for the toast
    const skippedNames = ONBOARDING_STEPS
      .filter((s) => skippedSteps.has(s.id))
      .map((s) => s.name);

    completeOnboarding();
    handleNavigate('admin-dashboard');

    if (skippedNames.length > 0) {
      toast.info(
        `Setup complete — ${skippedNames.length} step${skippedNames.length > 1 ? 's' : ''} skipped`,
        {
          description: `You skipped: ${skippedNames.join(', ')}. Visit the relevant Admin screens to configure these manually when you're ready.`,
          duration: 8000,
        },
      );
    }
  };

  // Edit: close assistant and navigate to real settings page
  const handleEdit = (stepId: OnboardingStepId) => {
    completeOnboarding();
    handleNavigate(EDIT_NAVIGATION[stepId]);
  };

  return (
    <div className="p-8 min-h-full">
      {/* Header */}
      <div className="flex flex-col items-center text-center mb-10">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-green-600" strokeWidth={2.5} />
        </div>
        <h2 className="text-2xl font-bold text-foreground">You're all set!</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Your workspace has been configured. Here's a summary of what was set up.
        </p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* LEFT — Setup Summary */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
            Setup Summary
          </h3>
          <div className="space-y-2">
            {ONBOARDING_STEPS.map((step) => {
              const status = getStepStatus(step.id);
              const isSkipped = skippedSteps.has(step.id);
              // Steps that never reached active are "not configured"
              const isNotConfigured = status === 'upcoming';

              return (
                <div
                  key={step.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-white"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Status indicator dot */}
                    <div
                      className={cn(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        isNotConfigured && 'bg-muted-foreground/30',
                        isSkipped && 'bg-amber-400',
                        !isSkipped && !isNotConfigured && 'bg-green-500',
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {step.name}
                      </p>
                      <p
                        className={cn(
                          'text-xs',
                          isNotConfigured && 'text-muted-foreground',
                          isSkipped && 'text-amber-600',
                          !isSkipped && !isNotConfigured && 'text-green-600',
                        )}
                      >
                        {isNotConfigured
                          ? 'Not configured'
                          : isSkipped
                            ? 'Skipped'
                            : 'Completed'}
                      </p>
                    </div>
                  </div>

                  {/* Edit link — navigates to real settings page, closes assistant */}
                  <button
                    type="button"
                    onClick={() => handleEdit(step.id)}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 hover:underline underline-offset-2 transition-colors flex-shrink-0"
                  >
                    Edit
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — What to do next */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
            What to do next
          </h3>
          <ol className="space-y-2.5">
            {getWhatToDoNext().map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center mt-0.5">
                  {idx + 1}
                </span>
                <p className="text-sm text-muted-foreground leading-snug">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* CTA */}
      <div className="flex flex-col items-center gap-3 mt-10">
        <Button size="lg" onClick={handleConfirm} className="px-8">
          Confirm Onboarding Complete →
        </Button>
      </div>
    </div>
  );
};

// ── What to do next items ─────────────────────────────────────────────────────
// Uses getForecastTerm() so the correct configured term is always shown.

function getWhatToDoNext(): string[] {
  const forecastTerm = getForecastTerm(false); // e.g. "forecast" / "projection"
  return [
    `Review your ${forecastTerm} baseline on the main dashboard`,
    `Connect additional data sources to improve ${forecastTerm} accuracy`,
    'Invite your finance team and assign them the right roles',
    'Configure child categories if needed (Settings → Parent Child Categories)',
    'Customise terminology to match your organisation (Settings → Terminology)',
  ];
}
